import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Handshake, Mail, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { apiClient } from "../lib/api";
import GoldServiceIcon from "../components/GoldServiceIcon";
import { OTHER_SERVICES } from "../data/otherServices";

/**
 * OtherServicesPage — iter252 (design refresh).
 *
 * Landing hub for the "خدمات أخرى" family. Reads sections from
 * data/otherServices.js so future additions are data-only. Ships with:
 *   1. Category tiles using bespoke GoldServiceIcon (no lucide clones)
 *   2. Per-tile waitlist email input — buyers get notified when the
 *      first accredited provider goes live in that category
 *   3. Provider onboarding invitation footer — routes prospective
 *      businesses straight into the existing partner-registration flow
 */
function WaitlistInput({ category, isAr }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/other-services/waitlist", {
        email: email.trim().toLowerCase(),
        category,
      });
      setDone(true);
      setEmail("");
      toast.success(
        isAr
          ? data.duplicate
              ? "بريدك مسجّل مسبقاً — سنبلّغك بأول مزوّد."
              : "تمّت إضافتك — سنبلّغك بأول مزوّد يصل."
          : data.duplicate
              ? "You're already on the list."
              : "You're on the list. We'll email you when the first provider goes live."
      );
    } catch (err) {
      toast.error(
        isAr ? "تعذّر التسجيل. حاول مرة أخرى." : "Could not add you to the list. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold w-full justify-center"
        style={{ backgroundColor: "#0a0a0a", color: "#d4b56e" }}
        data-testid={`waitlist-done-${category}`}
      >
        <Mail className="w-4 h-4" />
        {isAr ? "سنراسلك عند وصول أول مزوّد" : "We'll email you when the first provider lands"}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex items-stretch gap-2 rounded-full border border-[#0a0a0a]/12 bg-[#fafaf7] p-1"
      data-testid={`waitlist-form-${category}`}
      dir={isAr ? "rtl" : "ltr"}
    >
      <input
        type="email"
        required
        autoComplete="email"
        placeholder={isAr ? "بريدك الإلكتروني" : "your@email.com"}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="flex-1 bg-transparent px-3 py-1.5 text-xs sm:text-sm outline-none placeholder:text-[#9a9a9a] text-[#0a0a0a]"
        data-testid={`waitlist-email-${category}`}
      />
      <button
        type="submit"
        disabled={busy || !email.trim()}
        className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs sm:text-sm font-bold shrink-0 transition-opacity disabled:opacity-50"
        style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
        data-testid={`waitlist-submit-${category}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
        {isAr ? "أشعرني" : "Notify me"}
      </button>
    </form>
  );
}

export default function OtherServicesPage() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  // iter258 — fetch admin-added activity types and merge with the 3
  // system tiles. Admin additions never touch the "Partners" board;
  // they only surface here as new categories a business can pick.
  const [dynamic, setDynamic] = useState([]);
  // iter289 — Per-tile count of verified CARTAL partners pulled from
  // `platform_businesses`. Owners never need to re-register in
  // other_services_providers — as soon as they are verified they show
  // up here (and on every ProviderDirectoryPage sub-route) via the
  // same shared endpoint. Empty map is the safe default.
  const [verifiedCounts, setVerifiedCounts] = useState({});
  useEffect(() => {
    apiClient
      .get("/other-services/categories")
      .then(({ data }) => setDynamic(data.items || []))
      .catch(() => setDynamic([]));
  }, []);
  // Business-category lookup per section. Kept local to avoid a new
  // shared registry file — mirrors the 3 mappings ProviderDirectoryPage
  // already declares for the pre-launch routes.
  const CATEGORY_MAP = {
    delivery:  ["transport_company", "recovery_towing"],
    car_wash:  ["car_wash"],
    workshop:  ["workshop"],
  };
  useEffect(() => {
    let cancelled = false;
    const entries = Object.entries(CATEGORY_MAP);
    Promise.all(entries.map(([sec, cats]) =>
      Promise.all(cats.map((c) =>
        apiClient.get("/platform/public/companies", { params: { category: c, limit: 1 } })
          .then(({ data }) => Number(data?.total || 0))
          .catch(() => 0)
      )).then((counts) => [sec, counts.reduce((a, b) => a + b, 0)])
    )).then((pairs) => {
      if (cancelled) return;
      setVerifiedCounts(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, []);
  const sections = [
    ...OTHER_SERVICES,
    ...dynamic.map((c) => ({
      id: c.id,
      category: c.id,
      iconType: c.icon_type || "hub",
      to: `/other-services/${encodeURIComponent(c.id)}`,
      ar: { title: c.ar_title, desc: c.ar_desc },
      en: { title: c.en_title, desc: c.en_desc },
      testid: `other-dyn-${c.id}`,
    })),
  ];

  return (
    <section
      className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-14 md:py-20"
      data-testid="other-services-page"
      aria-label={isAr ? "بوابة مزودي الخدمات" : "Service Providers Portal"}
    >
      {/* Header */}
      <div className="flex items-start gap-4 mb-10 md:mb-14">
        <span
          className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl shrink-0"
          style={{
            backgroundColor: "#c9a55c15",
            border: "1.5px solid #c9a55c40",
          }}
          aria-hidden
        >
          <GoldServiceIcon type="hub" className="w-8 h-8 sm:w-9 sm:h-9" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="overline overline-gold mb-2 inline-block">
            {isAr ? "منظومة كارتال" : "CARTAL Ecosystem"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05] font-medium text-[#0a0a0a]">
            {isAr ? "بوابة مزودي الخدمات" : "Service Providers Portal"}
          </h1>
          <p className="mt-3 text-base sm:text-lg text-[#525252] leading-relaxed max-w-2xl">
            {isAr
              ? "اكتشف خدمات المركبات المقدمة من مزودي خدمات موثوقين في CARTAL."
              : "Discover vehicle services from trusted providers on CARTAL."}
          </p>
        </div>
      </div>

      {/* Grid of sections */}
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {sections.map((s) => {
          const t = isAr ? s.ar : s.en;
          const vcount = verifiedCounts[s.category] || 0;
          return (
            <li key={s.id}>
              <div className="flex flex-col h-full rounded-2xl border-2 border-[#0a0a0a]/8 bg-white p-5 sm:p-6 hover:border-[#c9a55c] hover:shadow-lg transition-all">
                <Link
                  to={s.to}
                  data-testid={s.testid}
                  className="group flex flex-col flex-1"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span
                      className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shrink-0"
                      style={{
                        backgroundColor: "#c9a55c15",
                        border: "1.5px solid #c9a55c40",
                      }}
                      aria-hidden
                    >
                      <GoldServiceIcon type={s.iconType} className="w-8 h-8" />
                    </span>
                    {vcount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                        style={{ backgroundColor: "#0a4f3a", color: "#ffffff" }}
                        data-testid={`verified-count-${s.category}`}
                      >
                        {vcount} {isAr ? "معتمد" : "verified"}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-[#0a0a0a] leading-tight mb-2">
                    {t.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#525252] leading-relaxed flex-1">
                    {t.desc}
                  </p>
                  <span
                    className="mt-5 inline-flex items-center gap-1 text-sm sm:text-base font-semibold text-[#8a6f2e]"
                  >
                    {isAr ? "استعرض المزوّدين" : "Browse providers"}
                    <ArrowRight className="w-4 h-4 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                  </span>
                </Link>
                {/* Per-tile waitlist — hidden when at least one verified
                    CARTAL partner already covers this section, since
                    the section is no longer "pre-launch" for buyers. */}
                {vcount === 0 && (
                  <div className="mt-5 pt-4 border-t border-dashed border-[#0a0a0a]/10">
                    <p className="text-xs sm:text-sm text-[#6b6b6b] mb-2 leading-relaxed">
                      {isAr
                        ? "هذا القسم في مرحلة تجهيز — اترك بريدك ليصلك أوّل المزوّدين المعتمدين."
                        : "This section is being prepared — leave your email to hear from us first."}
                    </p>
                    <WaitlistInput category={s.category} isAr={isAr} />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Partner onboarding invitation */}
      <div
        className="mt-14 md:mt-20 rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        style={{
          background: "linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 55%,#2b2116 100%)",
          border: "1px solid rgba(201,165,92,0.35)",
        }}
        data-testid="partner-invite-strip"
      >
        <span
          className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shrink-0"
          style={{
            background: "linear-gradient(135deg,rgba(212,181,110,0.15),rgba(138,111,46,0.10))",
            border: "1.5px solid rgba(201,165,92,0.55)",
          }}
          aria-hidden
        >
          <Handshake className="w-9 h-9 sm:w-10 sm:h-10" style={{ color: "#d4b56e" }} />
        </span>
        <div className="flex-1 min-w-0">
          <div
            className="overline mb-1.5 inline-block tracking-[0.22em]"
            style={{ color: "#c9a55c" }}
          >
            {isAr ? "دعوة للشركاء" : "Partners' invitation"}
          </div>
          <h2
            className="font-display text-xl sm:text-2xl lg:text-3xl tracking-[-0.01em] font-semibold leading-tight mb-2"
            style={{ color: "#f6f2ea" }}
          >
            {isAr
              ? "هل تدير خدمة نقل، عناية، أو صيانة؟ انضم كشريك معتمد"
              : "Run a transport, care, or maintenance business? Join as an accredited partner"}
          </h2>
          <p
            className="text-sm sm:text-base leading-relaxed max-w-2xl"
            style={{ color: "#c9c1b0" }}
          >
            {isAr
              ? "منصّة كارتال تجمعك بمالكي السيارات في كل مراحل الرحلة. تسجيل بسيط، تحقق سريع، ونطاق وصول وطني."
              : "CARTAL connects you to owners across every stage of the journey. Simple registration, fast vetting, national reach."}
          </p>
        </div>
        <Link
          to="/join?intent=partner&activity=delivery"
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shadow-lg hover:scale-[1.03] transition-transform shrink-0"
          style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
          data-testid="partner-invite-cta"
        >
          {isAr ? "سجّل كشريك معتمد" : "Register as a partner"}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </section>
  );
}
